import { ListRendererProps, TypeSerializerProps } from '@sanity/block-content-to-react'
import React, { FC } from 'react'
import styled, { css } from 'styled-components'

import Link from '../../global/Link'

interface MarkRendererProps<TMark extends Record<string, unknown>> {
  mark: TMark
}

const sharedStyles = css`
  margin-bottom: 15px;

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
`

const sharedHeadingStyles = css`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.text.primary};

  strong {
    color: #006072;
  }
`

export const Paragraph = styled.p`
  ${sharedStyles};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 20px;
  font-family: Roboto, sans-serif !important;
`
export const Heading1 = styled.h1`
  ${sharedStyles};
  ${sharedHeadingStyles};
`
export const Heading2 = styled.h2`
  ${sharedStyles};
  ${sharedHeadingStyles};
`
export const Heading3 = styled.h3`
  ${sharedStyles};
  ${sharedHeadingStyles};
`
export const Heading4 = styled.h4`
  ${sharedStyles};
  ${sharedHeadingStyles};
`
export const Heading5 = styled.h5`
  ${sharedStyles};
  ${sharedHeadingStyles};
`
export const Heading6 = styled.h6`
  ${sharedStyles};
  ${sharedHeadingStyles};
`

export const SubHeading = styled.p`
  ${sharedStyles};
  margin: 16px 0;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1.5;
  color: #179895;

  &:first-of-type {
    margin-top: 0;
  }
`
export const Blockquote = styled.blockquote`
  ${sharedStyles}
`
export const ListItem = styled.li`
  ${sharedStyles}
`
export const NumberedList = styled.ol`
  ${sharedStyles}
`
export const BulletList = styled.ul`
  ${sharedStyles}
`

export const Anchor = styled(Link)`
  ${sharedStyles};
  color: ${({ theme }) => theme.colors?.brand?.blue ?? 'inherit'};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const BlockRenderer: React.FC<TypeSerializerProps> = (props) => {
  const { children, node } = props
  switch (node.style) {
    case 'h1':
      return <Heading1>{children}</Heading1>
    case 'h2':
      return <Heading2>{children}</Heading2>
    case 'h3':
      return <Heading3>{children}</Heading3>
    case 'h4':
      return <Heading4>{children}</Heading4>
    case 'h5':
      return <Heading5>{children}</Heading5>
    case 'h6':
      return <Heading6>{children}</Heading6>
    case 'blockquote':
      return <Blockquote>{children}</Blockquote>
    case 'subheading1':
      return <SubHeading>{children}</SubHeading>
    case 'subheading2':
      return <SubHeading>{children}</SubHeading>
    case 'subheading3':
      return <SubHeading>{children}</SubHeading>
    case 'subheading4':
      return <SubHeading>{children}</SubHeading>
    case 'normal':
    default:
      return <Paragraph>{children}</Paragraph>
  }
}

const ListItemRenderer: React.FC = ({ children }) => <ListItem>{children}</ListItem>

const ListRenderer: React.FC<ListRendererProps> = ({ type, children }) =>
  type === 'number' ? <NumberedList>{children}</NumberedList> : <BulletList>{children}</BulletList>

const LinkMarkRenderer: FC<MarkRendererProps<{ href?: string; anchorHref?: string }>> = ({
  mark,
  children,
}) => {
  const { anchorHref, href } = mark
  return href ? (
    <Anchor to={href} rel="noopener noreferrer" target="_blank">
      {children}
    </Anchor>
  ) : (
    <Anchor to={anchorHref}>{children}</Anchor>
  )
}

const Figure = styled.figure`
  ${sharedStyles};
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 32px;
  &:last-of-type {
    margin-bottom: 0;
  }
`

const Img = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border: 0;
`

const Caption = styled.figcaption`
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.8;
`

const isObject = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && !Array.isArray(x) && x !== null

const MainImage: React.FC<TypeSerializerProps> = ({ node }) => {
  const url =
    isObject(node?.asset) && typeof node.asset.url === 'string' ? node.asset.url : undefined
  if (!url) {
    return null
  }
  const alt =
    typeof node?.alt === 'string' ? node.alt : typeof node?.caption === 'string' ? node.caption : ''
  return (
    <Figure>
      <Img src={url} alt={alt} loading="lazy" decoding="async" />
      {node?.caption ? <Caption>{node.caption}</Caption> : null}
    </Figure>
  )
}

export const serializers = {
  types: {
    block: BlockRenderer,
    mainImage: MainImage,
    image: MainImage,
  },
  marks: {
    link: LinkMarkRenderer,
  },
  list: ListRenderer,
  listItem: ListItemRenderer,
}
